import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { useState } from 'react';
import { selectFormOptions } from 'src/store/selectors/formOptions';
import { IContactFormDataForAPI, IContactPayloadForAPI } from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import { toast } from 'react-toastify';
import {
  addExistingPropertyDeveloper,
  deletePropertyDeveloper,
  fetchPropertyManagersByPropertyId,
  saveContact,
  updateContact,
} from 'src/services/client';
import {
  handleContactsAPIResponse, handleImageUpload, transformContactFormDataForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { buildPropertiesContactsPayload } from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/helpers';

export interface UseContactAPIOperationsPropertiesReturn {
  isLoading: boolean;
  response: any;
  error: string | null;
  
  onCreateContact: (
    formData: IContactFormData,
    propertyId: string,
    appendingNewContactToExistingProperty?: boolean
  ) => Promise<boolean>;
  
  onEditContact: (
    formData: IContactFormData,
    propertyId: string,
    editingExistingContactInTable?: boolean,
    appendingNewContactToExistingProperty?: boolean
  ) => Promise<boolean>;
  
  addExistingUserToProperty: (
    formData: IContactFormData,
    propertyId: string,
    adminContactId: string
  ) => Promise<boolean>;
  
  clearResponse: () => void;

  deletePropertyDeveloperFromProperty: (contactId: any, propertyId: any) => Promise<any>;
  isDeleting: boolean;

  fetchPropertyDevelopers: (propertyId: string) => Promise<void>;
  propertyDevelopers: any[];
  isFetchingDevelopers: boolean;
}

export const useContactAPIOperationsProperties = (): UseContactAPIOperationsPropertiesReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [propertyDevelopers, setPropertyDevelopers] = useState<any[]>([]);
  const [isFetchingDevelopers, setIsFetchingDevelopers] = useState(false);
  
  const adminUser = useSelector(selectAuthenticatedUser);
  const formOptions = useSelector(selectFormOptions);
  
  const onCreateContact = async (
    formData: IContactFormData,
    propertyId: string,
    appendingNewContactToExistingProperty?: boolean
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const imageUrl: string = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);
      
      const payload: IContactPayloadForAPI = buildPropertiesContactsPayload(
        apiFormData,
        propertyId,
        false,
        appendingNewContactToExistingProperty,
        formOptions,
        adminUser.id
      );
      
      const responseData = await saveContact(payload);
      return handleContactsAPIResponse(responseData, true, setResponse, setError);
    } catch (err: any) {
      console.error('Contact creation failed:', err);
      
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      setError(errorMessage);
      
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const onEditContact = async (
    formData: IContactFormData,
    propertyId: string,
    editingExistingContactInTable: boolean = false,
    appendingNewContactToExistingProperty?: boolean
  ): Promise<boolean> => {
    if (!formData.id) {
      const error = 'Cannot edit contact: Missing contact ID';
      console.error(error);
      toast.error(`🚫 ${error}`, {
        autoClose: 8000,
        position: 'top-center',
      });
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const imageUrl: string = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);
      
      const payload: IContactPayloadForAPI = buildPropertiesContactsPayload(
        apiFormData,
        propertyId,
        editingExistingContactInTable,
        appendingNewContactToExistingProperty,
        formOptions,
        adminUser.id
      );
      
      const responseData: any = await updateContact(payload);
      return handleContactsAPIResponse(responseData, true, setResponse, setError);
    } catch (err: any) {
      console.error('Contact update failed:', err);
      
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      setError(errorMessage);
      
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const addExistingUserToProperty = async (
    formData: IContactFormData,
    propertyId: string,
    adminContactId: string
  ): Promise<boolean> => {
    const requestPayload = {
      PropertyId: propertyId,
      PropertyManagerId: formData.id,
      UserId: adminContactId,
      Role: formData.userRole,
      IsPrimaryContact: formData.isPrimaryContact,
    };
    
    try {
      const response: any = await addExistingPropertyDeveloper(requestPayload);
      
      if (response.status === 200 || response.ok) {
        const responseJson = await response.json();
        
        if (responseJson.statusCode === 200) {
          toast.success('ⓘ User added to property successfully!', {
            autoClose: 10000,
            position: 'top-center',
          });
          return true;
        } else {
          throw new Error(
            `API returned status code: ${responseJson.statusCode}`
          );
        }
      } else {
        throw new Error(`Request failed with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Adding existing user to property failed:', err);
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      throw err;
    }
  };

  const deletePropertyDeveloperFromProperty = async (
    contactId: any,
    propertyId: any
  ): Promise<any> => {
    setIsDeleting(true);

    try {
      const response: any = await deletePropertyDeveloper(contactId, propertyId);

      let data: any = {};

      if (response.headers.get('content-type')?.includes('application/json')) {
        data = await response.json();
      }

      // This endpoint is currently bad. So Check if response body contains an error even if status is 200.
      if (data.statusCode === 403 || data.statusCode >= 400) {
        toast.error(
          '🚫 We were unable to delete the contact due to a temporary server issue. Please try again shortly. ' +
          'If this issue persists please Contact Juniper support',
          {
            autoClose: 12000,
            position: 'top-center',
          }
        );
        setIsDeleting(false);
        return { success: false, error: data.message };
      }

      // Check HTTP status code in the response body JSON.
      if (!response.ok && response.status !== 200) {
        toast.error(
          '🚫 Something went wrong with deleting a contact, please try again.',
          {
            autoClose: 12000,
            position: 'top-center',
          }
        );
        setIsDeleting(false);
        return { success: false, error: 'Request failed' };
      }

      toast.success('ⓘ The user has been deleted.', {
        autoClose: 10000,
        position: 'top-center',
      });

      setIsDeleting(false);
      return { success: true };
    } catch (error) {
      console.error('Error deleting property developer:', error);
      toast.error('🚫 Something went wrong, please try again.', {
        autoClose: 12000,
        position: 'top-center',
      });

      setIsDeleting(false);
      return { success: false, error };
    }
  }
  
  const clearResponse = () => {
    setResponse(null);
    setError(null);
  };

  const fetchPropertyDevelopers = async (propertyId: string): Promise<void> => {
    setIsFetchingDevelopers(true);

    try {
      const response: any = await fetchPropertyManagersByPropertyId(propertyId);

      if (response.status === 200 || response.ok) {
        const data = await response.json();
        setPropertyDevelopers(data);
      } else {
        console.error('Failed to fetch property developers:', response.status);
        toast.error('🚫 Failed to load property developers. Please refresh the page.', {
          autoClose: 8000,
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('Error fetching property developers:', error);
      toast.error('🚫 Failed to load property developers. Please refresh the page.', {
        autoClose: 8000,
        position: 'top-center',
      });
    } finally {
      setIsFetchingDevelopers(false);
    }
  };
  
  return {
    isLoading,
    response,
    error,
    onCreateContact,
    onEditContact,
    addExistingUserToProperty,
    clearResponse,
    deletePropertyDeveloperFromProperty,
    isDeleting,
    fetchPropertyDevelopers,
    propertyDevelopers,
    isFetchingDevelopers,
  };
}